import { type ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import {
  Badge, Button,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  useMediaQuery, useTheme
} from "@mui/material";
import {
  AssignmentLate, Article, AssistantDirection,
  CurrencyYen, Dashboard, PersonAdd, PersonAddOutlined,
  Storefront, Workspaces
} from "@mui/icons-material";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import AdminPageFrame from "../components/AdminPageFrame";
import ContainerPaper from "../components/ContainerPaper";
import Loading from "../components/Loading";
import Section from "../components/Section";
import apiServices from "../services/apiServices";
import { isAdmin, isNavigator } from "../util/util";

type Role = "navigator"
type SubMenu = {
  key: string;
  roles?: Role[];
}
type Menu = {
  key: string;
  icon: ReactElement;
  roles?: Role[];
  children: SubMenu[];
};

const menuList: Menu[] = [
  {
    key: "dashboard",
    icon: <Dashboard />,
    children: [
      { key: "statistics" }
    ]
  },
  {
    key: "management_users",
    icon: <PersonAddOutlined />,
    roles: ["navigator"],
    children: [
      { key: "users", roles: ["navigator"]  }
    ]
  },
  {
    key: "public_users",
    icon: <PersonAdd />,
    roles: ["navigator"],
    children: [
      // { key: "account_suspension" },
      { key: "user_notifications", roles: ["navigator"] },
      { key: "member_groups", roles: ["navigator"] }
    ]
  },
  {
    key: "data_definitions",
    icon: <Workspaces />,
    roles: ["navigator"],
    children: [
      { key: "categories" },
      { key: "groups" },
      { key: "sample_images", roles: ["navigator"] }
    ]
  },
  {
    key: "negative_words",
    icon: <AssignmentLate />,
    roles: ["navigator"],
    children: [
      { key: "negative_words" },
      { key: "pending_posts", roles: ["navigator"]  },
      { key: "rejected_posts", roles: ["navigator"] },
      { key: "pending_comments" },
      { key: "rejected_comments" }
    ]
  },
  // {
  //   key: "shops",
  //   icon: <Storefront />,
  //   children: [
  //     { key: "qr_payment" },
  //     { key: "event_notification" }
  //   ]
  // },
  {
    key: "money",
    icon: <CurrencyYen />,
    children: [
      { key: "payment" }
    ]
  },
  {
    key: "posts",
    icon: <Article />,
    roles: ["navigator"],
    children: [
      { key: "everyone_posts", roles: ["navigator"] },
      { key: "post_events", roles: ["navigator"] }
    ]
  },
  {
    key: "navigator",
    icon: <AssistantDirection />,
    roles: ["navigator"],
    children: [
      { key: "suggestions", roles: ["navigator"] },
      { key: "navigator_posts", roles: ["navigator"] },
    ]
  }
];

type Status = {
  count: number;
  loading: boolean;
};
type MenuStatus = {[key: string]: Status};

export default function AdminHome() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { admin } = useSelector((state: any) => state.adminAuth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconStyle = { color: "white", marginLeft: isMobile ? 0 : 1 }

  const [menuStatus, setMenuStatus] = useState<MenuStatus>({ pending_posts: {count: 0, loading: false} });

  useEffect(() => {
    if (isAdmin(admin)) fetchPendingPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingPost = async () => {
    try {
      setMenuStatus((prev: MenuStatus) => {return {...prev, pending_posts: {count: 0, loading: true}}});
      const postsCount = await apiServices.getPendingPostsCount();
      setMenuStatus((prev: MenuStatus) => {return {...prev, pending_posts: {count: postsCount.pending_posts_count, loading: false}}});
    } catch (error: any) {
      dispatch(setErrorObj(error))
      setMenuStatus((prev: MenuStatus) => {return {...prev, pending_posts: {count: 0, loading: false}}});
    }
  };

  const isAllowed = (roles?: Role[]): boolean => {
    if (isAdmin(admin)) return true
    if (isNavigator(admin) && roles?.find((r: Role) => r === "navigator")) return true
    return false;
  }

  const handleMenuClick = (menu: Menu, subMenu: SubMenu) => {
    if (!isAllowed(menu.roles)) return
    navigate(`/mng/${menu.key}/${subMenu.key}`);
  };

  const menuItem = (menu: Menu, subMenu: SubMenu, index: number): ReactElement | undefined => {
    if (!isAllowed(subMenu.roles)) return undefined;

    const status = menuStatus[subMenu.key];
    return (
      <ListItem key={index} sx={styles.menuContainer}>
        <Loading visible={status ? status.loading : false} />
        <ListItemButton onClick={() => handleMenuClick(menu, subMenu)}>
          {status &&
            <Badge badgeContent={status.count} color="error">
              <PendingActionsIcon
                sx={iconStyle}
              />
            </Badge>
          }
          <Button variant="outlined" sx={styles.menuLabel}>
            {T(`mng_${menu.key}_${subMenu.key}`)}
          </Button>
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <AdminPageFrame>
      <ContainerPaper>
        <Section title={T('mng_menu')}>
          <List sx={styles.container}>
            {menuList.map((menu: Menu, i: number) => {
              if (!isAllowed(menu.roles)) return undefined;
              return (
                <ListItem sx={styles.categoryContainer} key={i}>
                  <ListItemButton component="h3" disableRipple>
                    <ListItemIcon>{menu.icon}</ListItemIcon>
                    <ListItemText primary={T(`mng_${menu.key}`)} />
                  </ListItemButton>
                  <List sx={styles.menuListContainer}>
                    {menu.children.map((subMenu: SubMenu, j: number) => menuItem(menu, subMenu, j))}
                  </List>
                </ListItem>
              );
            })}
          </List>
        </Section>
      </ContainerPaper>
    </AdminPageFrame>
  );
};

const styles = {
  container: {
    display: "flex",
    flexWrap: "wrap",
    width: '100%',
  },
  categoryContainer: {
    flexDirection: "column",
    alignItems: "start",
    "& h3": {
      width: "100%",
      paddingBottom: 0,
      borderBottom: "1px solid",
      borderColor: "text.primary",
      cursor: "initial",
    },
    "& h3:hover": {
      backgroundColor: "transparent",
    }
  },
  menuListContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    width: '100%',
  },
  menuContainer: {
    width: '200px',
    "& .MuiListItemButton-root": {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      height: "100%",
    },
    "& .MuiBadge-root": {
      position: "absolute",
      top: "5px",
      right: "15px"
    }
  },
  menuLabel: {
    height: "100%",
    textAlign: "center",
  }
};
